import React, { useEffect, useMemo, useState } from 'react';
import LanguageSwitcher from '../components/LanguageSwitcher/LanguageSwitcher';
import { fetchPasswordResetTokenStatus, resetPassword } from '../auth/passwordReset';
import { useI18n } from '../i18n/I18nContext';
import PasswordInput from '../components/PasswordInput/PasswordInput';
import './ResetPasswordPage.css';

interface ResetPasswordPageProps {
  showToast: (message: string, severity?: 'success' | 'error' | 'warning' | 'info') => void;
}

type TokenStatus = {
  valid: boolean;
  email: string | null;
  displayName: string | null;
  expiresAt: string | null;
  error: string | null;
};

const ResetPasswordPage: React.FC<ResetPasswordPageProps> = ({ showToast }) => {
  const { t } = useI18n();
  const token = useMemo(() => new URLSearchParams(window.location.search).get('token') ?? '', []);
  const [status, setStatus] = useState<TokenStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadStatus() {
      if (!token) {
        setStatus({
          valid: false,
          email: null,
          displayName: null,
          expiresAt: null,
          error: 'invalid',
        });
        setLoading(false);
        return;
      }

      try {
        const nextStatus = await fetchPasswordResetTokenStatus(token);
        if (!cancelled) {
          setStatus(nextStatus);
        }
      } catch {
        if (!cancelled) {
          setStatus({
            valid: false,
            email: null,
            displayName: null,
            expiresAt: null,
            error: 'invalid',
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadStatus();

    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    if (!token) {
      showToast(t('passwordReset.invalidToken'), 'error');
      return;
    }

    if (!password.trim()) {
      showToast(t('passwordReset.requirePassword'), 'error');
      return;
    }

    if (password.length < 8) {
      showToast(t('passwordReset.minPassword'), 'error');
      return;
    }

    if (password !== confirmPassword) {
      showToast(t('passwordReset.passwordMismatch'), 'error');
      return;
    }

    setSubmitting(true);
    try {
      await resetPassword(token, password);
      showToast(t('passwordReset.resetSuccess'), 'success');
      setStatus((current) => current ? { ...current, valid: false, error: 'used' } : current);
      setPassword('');
      setConfirmPassword('');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('passwordReset.resetError'), 'error');
    } finally {
      setSubmitting(false);
    }
  }

  function goToLogin() {
    window.location.assign('/');
  }

  return (
    <div className="auth-page">
      <div className="auth-card reset-password-page">
        <div className="auth-card__language">
          <LanguageSwitcher minWidth={140} />
        </div>

        {loading ? (
          <>
            <h1>{t('passwordReset.validatingTitle')}</h1>
            <p className="auth-card__intro">{t('passwordReset.validatingBody')}</p>
          </>
        ) : status?.valid ? (
          <>
            <h1>{t('passwordReset.resetTitle')}</h1>
            <p className="auth-card__intro">
              {status.displayName
                ? t('passwordReset.resetDescriptionNamed').replace('{name}', status.displayName)
                : t('passwordReset.resetDescription')}
            </p>
            <form onSubmit={handleSubmit}>
              <label className="reset-password-page__field">
                <span>{t('passwordReset.newPassword')}</span>
                <PasswordInput
                  value={password}
                  onChange={setPassword}
                  usernameValue={status.email ?? ''}
                  autoComplete="new-password"
                />
              </label>
              <label className="reset-password-page__field">
                <span>{t('passwordReset.confirmPassword')}</span>
                <PasswordInput
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  usernameValue={status.email ?? ''}
                  autoComplete="new-password"
                />
              </label>
              <div className="reset-password-page__actions">
                <button type="button" className="secondary" onClick={goToLogin}>
                  {t('passwordReset.backToLogin')}
                </button>
                <button type="submit" className="primary" disabled={submitting}>
                  {submitting ? t('passwordReset.resetting') : t('passwordReset.resetButton')}
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <h1>{status?.error === 'used' ? t('passwordReset.usedTitle') : t('passwordReset.invalidTitle')}</h1>
            <p className="auth-card__intro">
              {status?.error === 'expired'
                ? t('passwordReset.expiredBody')
                : status?.error === 'used'
                  ? t('passwordReset.usedBody')
                  : t('passwordReset.invalidBody')}
            </p>
            <button type="button" className="reset-password-page__back-button" onClick={goToLogin}>
              {t('passwordReset.backToLogin')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
